import { useEffect } from "react";
import { Header } from "@/layouts/Header";
import { GoogleReviewsBadge } from "@/components/shared/GoogleReviewsBadge";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";
import { getUTMData } from "@/lib/utm";
import { Check, Star, Shield, Users, Globe, Zap, AlertTriangle, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const BookConsultation = () => {
  useEffect(() => {
    // Dynamically embed GHL resizing script for the booking widget
    const scriptId = "frayze-booking-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://api.frayze.ca/js/form_embed.js";
      script.type = "text/javascript";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <SEOHead
        title="Book Growth System Strategy Call | Frayze"
        description="We design CRM, automation, and AI systems that eliminate missed leads and create predictable revenue. Book your strategy call to see if we're a fit."
        keywords="growth strategy call, CRM consultation, business automation audit, Canadian digital agency, sales system design"
        ogTitle="Book Growth System Strategy Call | Frayze"
        ogDescription="We design CRM, automation, and AI systems that eliminate missed leads and create predictable revenue. Book your strategy call to see if we're a fit."
        twitterTitle="Book Growth System Strategy Call | Frayze"
        twitterDescription="We design CRM, automation, and AI systems that eliminate missed leads and create predictable revenue. Book your strategy call to see if we're a fit."
        canonical="https://frayze.ca/book-consultation/"
        robots="noindex, follow"
      />

      <main className="pt-40 md:pt-48 pb-20">
        <div className="container mx-auto px-4 md:px-6">
          {/* Header Section */}
          <div className="max-w-4xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-4 px-4 py-1.5 text-sm border-primary/20 bg-primary/5 text-primary">
              20-Minute Strategy Session
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 tracking-tight text-foreground">
              Book Your <span className="bg-gradient-cta bg-clip-text text-transparent">Growth System Strategy Call</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              Stop losing leads to slow follow-up. In 20 minutes, we will map a CRM, automation, and AI system that turns more of your leads into booked revenue. <span className="text-foreground font-medium">Let's see if we're a fit.</span>
            </p>
            <div className="flex justify-center mt-6">
              <GoogleReviewsBadge />
            </div>
          </div>

          {/* Qualification Grid */}
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 mb-16">
            {/* What This Call Covers */}
            <Card className="border-primary/10 shadow-lg bg-card/50 backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2.5 rounded-lg bg-primary/10">
                    <Zap className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold">What This Call Covers</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  In 20 minutes, you'll see exactly where revenue is leaking and how a unified growth system plugs the gaps.
                </p>
                <ul className="space-y-4">
                  {[
                    "Identify where revenue is leaking in your pipeline",
                    "Review your follow-up speed and lead capture",
                    "Assess your CRM or current tech stack",
                    "Outline what a unified AI growth system would look like",
                    "Decide if our systems are the right fit for your goals"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm font-medium">
                      <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Who This Is For */}
            <Card className="border-primary/10 shadow-lg bg-card/50 backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2.5 rounded-lg bg-blue-500/10">
                    <Users className="w-6 h-6 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-bold">This Call Is Best For</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  You'll get the most from this call if you're an established operator ready to scale.
                </p>
                <ul className="space-y-4">
                  {[
                    "Trades & Contractors scaling past word-of-mouth",
                    "Service Businesses hitting a revenue plateau",
                    "High-Ticket Sales Operations needing better tracking",
                    "Multi-Location Businesses struggling with consistency",
                    "Companies doing $500K+ annual revenue"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm font-medium">
                      <ArrowRight className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Disqualifier */}
          <div className="max-w-3xl mx-auto mb-16">
            <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-6 flex flex-col md:flex-row gap-6 items-start">
              <div className="p-3 rounded-full bg-destructive/10 shrink-0">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2 text-red-700">Not Ideal If:</h4>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                  <li className="text-sm text-muted-foreground">• You're looking for free advice only</li>
                  <li className="text-sm text-muted-foreground">• You're not ready to invest in systems</li>
                  <li className="text-sm text-muted-foreground">• You need a cheap one-page brochure site</li>
                  <li className="text-sm text-muted-foreground">• You are just starting with $0 revenue</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Booking Widget: min-height floor (taller on mobile) so the GHL auto-resize
              script can never collapse the iframe to 0 on small screens / SPA nav. */}
          <div className="max-w-4xl mx-auto bg-card rounded-2xl shadow-xl border border-border/50 overflow-hidden mb-24">
            <iframe
              src={`https://api.frayze.ca/widget/booking/sJpmHE1UUjYl00tZdlot${typeof window !== 'undefined' ? window.location.search : ''}`}
              className="block w-full border-0 min-h-[1000px] sm:min-h-[760px]"
              scrolling="yes"
              id="sJpmHE1UUjYl00tZdlot_1780808780327"
              title="Book Strategy Call"
            />
          </div>

          {/* Trust Section - Why Businesses Work With Frayze */}
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Why Businesses Work With Frayze</h2>
              <p className="text-muted-foreground text-lg">Systems-first approach. Built for real operators.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {[
                {
                  icon: Zap,
                  title: "Unified Stack",
                  desc: "CRM + Automation + Content in one system."

                },
                {
                  icon: Globe,
                  title: "Canadian Support",
                  desc: "Built in Thunder Bay, supported locally."
                },
                {
                  icon: Shield,
                  title: "Enterprise Grade",
                  desc: "Infrastructure that scales to millions."
                },
                {
                  icon: Users,
                  title: "Trades Focused",
                  desc: "Designed for service operations, not fluff."
                }
              ].map((feature, i) => (
                <div key={i} className="p-6 rounded-xl border bg-card/50 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </div>
              ))}
            </div>

            {/* Testimonial */}
            <div className="bg-gradient-to-br from-primary/5 via-primary/10 to-transparent rounded-2xl p-8 md:p-12 text-center max-w-3xl mx-auto border border-primary/10">
              <div className="flex justify-center mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-6 h-6 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <blockquote className="text-xl md:text-2xl font-medium mb-6 leading-relaxed">
                "An awesome AI company with high potential and best capabilities in town to transform your business from zero to hero."
              </blockquote>
              <div className="flex items-center justify-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                  <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground font-bold text-lg">LK</div>
                </div>
                <div className="text-left">
                  <div className="font-bold">Lokendra</div>
                  <div className="text-sm text-muted-foreground">Marketing Lead - Need a Car</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

    </div>
  );
};

export default BookConsultation;